import { app } from '@microsoft/teams-js';

/**
 * Configuration de l'URL de l'API backend
 * Format: http://host:port/api/ (with trailing slash)
 * IMPORTANT: VITE_API_BASE_URL must be configured in .env or .localConfigs
 * En développement : http://localhost:3978/api/
 * En production : https://your-backend.azurewebsites.net/api/
 */
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
if (!apiBaseUrl || apiBaseUrl.trim() === '') {
  console.warn('[API Config] VITE_API_BASE_URL is not configured. Value:', apiBaseUrl);
} else {
  console.log('[API Config] VITE_API_BASE_URL loaded successfully:', apiBaseUrl);
}
export const API_BASE_URL = apiBaseUrl || '';

/**
 * Configuration Azure AD pour l'application ISV
 * IMPORTANT: ISV_AAD_APP_CLIENT_ID must be configured in env/.env.local
 * Cette variable est utilisée pour les flux d'authentification et de consentement admin
 *
 * DEBUG: Log the value to help troubleshoot configuration issues
 */
const rawValue = import.meta.env.VITE_ISV_AAD_APP_CLIENT_ID;
if (typeof rawValue === 'string' && rawValue.trim()) {
  console.log('[API Config] ISV_AAD_APP_CLIENT_ID loaded successfully');
} else {
  console.warn('[API Config] ISV_AAD_APP_CLIENT_ID is not configured. Value:', rawValue);
}
export const ISV_AAD_APP_CLIENT_ID = rawValue || '';

/**
 * Obtenir le token d'authentification Teams
 */
export async function getAuthToken(): Promise<string> {
  try {
    await app.initialize();
    const token = await (app as any).authentication.getAuthToken();
    return token;
  } catch (error) {
    console.error('[API] Failed to get auth token:', error);
    throw new Error('Authentication failed');
  }
}

/**
 * Client API générique avec authentification automatique
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    // Validate API configuration
    if (!API_BASE_URL || API_BASE_URL.trim() === '') {
      throw new Error(
        'API Configuration Error: VITE_API_BASE_URL is not configured.\n' +
        'Please set VITE_API_BASE_URL in your .env file or .localConfigs.\n' +
        'Example: http://localhost:3978/api/'
      );
    }

    // Obtenir le token Teams
    const token = await getAuthToken();
    
    // Construire l'URL complète
    const url = `${API_BASE_URL.replace(/\/$/, '')}${endpoint}`;
    
    // Effectuer la requête avec le token
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
      credentials: 'include',
    });

    // Gérer les erreurs HTTP
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `API request failed: ${response.statusText}`
      );
    }

    // Retourner les données JSON
    return response.json();
  } catch (error) {
    console.error(`[API] Request failed for ${endpoint}:`, error);
    throw error;
  }
}

/**
 * Helpers pour les méthodes HTTP courantes
 */
export const api = {
  get: <T>(endpoint: string) => 
    apiRequest<T>(endpoint, { method: 'GET' }),
  
  post: <T>(endpoint: string, data?: any) =>
    apiRequest<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),
  
  put: <T>(endpoint: string, data?: any) =>
    apiRequest<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),
  
  delete: <T>(endpoint: string) =>
    apiRequest<T>(endpoint, { method: 'DELETE' }),
};
